import { defa }               from 'defa-object';
import SuccessView            from 'scripts/defa/basic/view-success-view';
import ClaimForwardEmailForm  from 'scripts/defa/claim/view-claim-forward-email-form';
import ClaimForwardOptionForm from 'scripts/defa/claim/view-claim-forward-option-form';
import ClaimOverviewForm      from 'scripts/defa/claim/view-claim-overview-form';
import ClaimTermsForm         from 'scripts/defa/claim/view-claim-terms-form';
import ClaimTransferForm      from 'scripts/defa/claim/view-claim-transfer-form';
import Customer               from 'scripts/defa/customers/model-customer';
import CustomerContainer      from 'scripts/defa/customers/view-customer-container';
import PhoneNumber            from 'scripts/defa/phone-numbers/model-phone-number';
import PhoneNumberCreateForm  from 'scripts/defa/phone-numbers/view-phone-number-create-form';
import SettingsAutomotive     from 'scripts/defa/settings-automotive/model-settings-automotive';
import SettingsAutomotiveForm from 'scripts/defa/settings-automotive/view-settings-automotive-form';
import User                   from 'scripts/defa/users/model-user';
import UserContainer          from 'scripts/defa/users/view-user-container';

import Flow                   from 'scripts/poppet/flow';
import template               from 'templates/claimFlow.jst.html';

const channelLayout = Bb.Radio.channel( 'layout' ),
	channelUser   = Bb.Radio.channel( 'user' );

const channel = Bb.Radio.channel( 'objects' );

const selectors = {
	container: '[data-region=claim-container]',
};

/**
 * defa.views.ClaimFlow
 * The Flow to dispatch all types of claims.
 * @version 1.2.1
 */
const ClaimFlow = Flow.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template: template,
	
	regions: {
		container: selectors.container,
	},
	
	
	// ------------------------------------------------
	// Event handlers
	// ------------------------------------------------
	
	/**
	 * Triggers when a childView opens an installer.
	 * @void
	 */
	onChildviewInstallerOpen: function ( id ) {
		this.installer = id;
		this.dispatch();
	},
	
	/**
	 * Triggers when a childView opens a customer.
	 * @void
	 */
	onChildviewCustomerOpen: function ( id ) {
		this.owner = id;
		this.dispatch();
	},
	
	
	/**
	 * Triggers when a childView asks to change the alarmGroup.
	 * @void
	 */
	onChildviewAlarmGroupChange: function ( alarmGroups ) {
		this.alarmGroups = alarmGroups;
		this._showInsuranceCompanySelection();
	},
	
	/**
	 * Triggers when a childView asks to clear the alarmGroup.
	 * @void
	 */
	onChildviewAlarmGroupClear: function () {
		let self = this;
		
		self.listenToOnce( self.settingsAutomotive, 'sync', function () {
			
			self.model.set( {
				transientInsuranceCompany   : false,
				transientAlarmCentral       : false,
				transientSubscriptionPackage: false,
			} );
			
			self.alarmSelected = true;
			self.dispatch();
			
		} );
		
		self.settingsAutomotive.removeAlarmGroup( {
			transientInstaller: self.installer,
		} );
	},
	
	/**
	 * Triggers when a childView asks to skip the alarmGroup options.
	 * @void
	 */
	onChildviewAlarmGroupSkip: function () {
		
		let self = this;
		
		// no alarm group set, just skip ahead.
		if ( !self.model.get( 'transientAlarmCentral' ) ) {
			self.alarmSelected = true;
			self.dispatch();
			
			// an alarm group is set, get a token before progressing.
		} else {
			
			self.listenToOnce( self.model, 'sync', function () {
				
				self.alarmSelected = true;
				self.dispatch();
				self.options.token = self.model.get( 'token' );
			} );
			
			self.model.getToken( {
				installer: self.installer,
			} );
		}
	},
	
	
	/**
	 * Triggers when a childView changes the insuranceCompany service.
	 * @void
	 */
	onChildviewInsuranceCompanyService: function ( service ) {
		this.service = service;
	},
	
	/**
	 * Triggers when a childView selects an insuranceCompany.
	 * @void
	 */
	onChildviewInsuranceCompanySelected: function ( id ) {
		this.insuranceCompany = id;
		this._showAlarmCentralSelection();
	},
	
	
	/**
	 * Triggers when a childView selects an alarmCentral.
	 * @void
	 */
	onChildviewAlarmCentralSelected: function ( id ) {
		this.alarmCentral = id;
		this._saveAlarmGroup();
	},
	
	
	/**
	 * Triggers when a childView requests to clear the prepaid subscription package.
	 * @void
	 */
	onChildviewPrepaidPackageClear: function () {
		
		this.listenToOnce( this.model, 'sync', function () {
			
			this.model.set( { transientSubscriptionPackage: false } );
			
			this.prepaidSelected = true;
			this.dispatch();
		} );
		
		this.model.removeSubscriptionPackage( {
			installer: this.installer,
		} );
	},
	
	/**
	 * Triggers when a childView has selected a prepaid subscription package.
	 * @void
	 */
	onChildviewPrepaidPackageSelected: function ( id ) {
		
		this.subscriptionPackage = channel.request( 'subscriptionPackages' ).get( id );
		
		this.listenToOnce( this.model, 'sync', function () {
			this.prepaidSelected = true;
			this.forward         = 'forward';
			this.dispatch();
		} );
		
		this.model.saveSubscriptionPackage( {
			transientSubscriptionPackage: this.subscriptionPackage.get( 'id' ),
			promotionCode               : this.subscriptionPackage.get( 'promotionCode' ),
			installer                   : this.installer,
		} );
	},
	
	/**
	 * Triggers when a childView requests to skip the prepaid subscription package.
	 * @void
	 */
	onChildviewPrepaidPackageSkip: function () {
		
		// if a subscription package is set, the only way out is to forward.
		if ( this.model.get( 'transientSubscriptionPackage' ) ) {
			this.forward = 'forward';
		}
		
		this.prepaidSelected = true;
		this.dispatch();
	},
	
	/**
	 * Triggers when a childView triggers retest.
	 * @void
	 */
	onChildviewRetest: function(){
		this.trigger( 'nav', 'unit:test', [ this.model.get( 'id' ), ] );
	},
	
	/**
	 * Triggers when a childView has selected a subscriptionPackage.
	 * @void
	 */
	onChildviewSubscriptionPackageSelected: function ( id ) {
		
		this.subscriptionPackage   = channel.request( 'subscriptionPackages' ).get( id );
		this.subscriptionSelection = true;
		this.dispatch();
	},
	
	
	/**
	 * Triggers when a childView requests to change the forward option.
	 * @void
	 */
	onChildviewClaimForwardOption: function ( value ) {
		this.forward = value;
		this.dispatch();
	},
	
	/**
	 * Triggers when a childView requests to accept the claim.
	 * @void
	 */
	onChildviewClaimAcceptTotals: function () {
		this.acceptTotals = true;
		this.dispatch();
	},
	
	/**
	 * Triggers when a childView requests to forward the unit by email.
	 * @void
	 */
	onChildviewClaimForwardEmail: function ( email ) {
		
		this.listenToOnce( this.model, 'success', function () {
			this.completed = true;
			this.dispatch();
		} );
		
		this.model.saveForward( {
			email    : email,
			installer: this.installer,
		} );
	},
	
	/**
	 * Triggers when a childView requests to accept the terms and conditions for a subscription.
	 * @void
	 */
	onChildviewClaimAcceptTerms: function () {
		this.acceptTerms = true;
		this.dispatch();
	},
	
	/**
	 * Triggers when a childView requests a claim transfer acceptance.
	 * @void
	 */
	onChildviewClaimAcceptTransfer: function () {
		this.acceptTransfer = true;
		this.dispatch();
	},
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	// - dispatches and flows
	
	/**
	 * Decides which view to show based on information available to the layout.
	 * @void
	 */
	dispatch: function () {
		let self = this;
		
		self.user       = channelUser.request( 'model' );
		self.installers = self.user.getCustomers();
		self.loggedIn   = channelUser.request( 'is:logged:in' );
		self.family     = self.model.get( 'family' );
		
		// setup AUTOMOTIVE specific stuff
		if ( 'AUTOMOTIVE' === self.family ) {
			self.settingsAutomotive = ( self.model.get( 'transientSettingsAutomotive' ) )
									  ? channel.request( 'settingsAutomotives' ).get( self.model.get( 'transientSettingsAutomotive' ) )
									  : new SettingsAutomotive( { unit: self.model.get( 'id' ) } );
			
			// if options.email, attempt to set self.name by settingsautomotive.
			if ( this.options.email && !self.name ) {
				// if settingsautomotive got everything
				if ( self.settingsAutomotive.get( 'type' )
					 && self.settingsAutomotive.get( 'brand' )
					 && self.settingsAutomotive.get( 'model' )
					 && self.settingsAutomotive.get( 'registrationNumber' ) ) {
					
					// set the name
					self.name = self.model.get( 'alias' );
				}
			}
		}
		
		/*
		 // dispatch vars - can be commented out when done.
		 self.name                 = self.name                 || !1;
		 self.alarmSelected        = self.alarmSelected        || !1;
		 self.installer            = self.installer            || !1;
		 self.alarmGroups          = self.alarmGroups          || !1;
		 self.subscriptionPackages = self.subscriptionPackages || !1;
		 self.insuranceCompany     = self.insuranceCompany     || !1;
		 self.alarmCentral         = self.alarmCentral         || !1;
		 self.service              = self.service              || !1;
		 self.prepaidSelected      = self.prepaidSelected      || !1;
		 /**/
		
		// separate the current dispatches with some simple rules
		let rules = {
			token              : self.options.token,
			alarmGroup         : self.model.get( 'transientAlarmCentral' ),
			subscription       : self.model.get( 'transientSubscription' ),
			subscriptionPackage: self.model.get( 'transientSubscriptionPackage' ),
		};
		
		// if unit has token but no subscription, and package is a number (because 0 is allowed), do the forward
		// subscription routine.
		if ( rules.token && !rules.subscription && _.isNumber( rules.subscriptionPackage ) ) {
			// skip option of selecting subscription package.
			self.prepaid               = true;
			self.subscriptionSelection = true;
			self.subscriptionPackage   = channel.request( 'subscriptionPackages' ).get( self.model.get( 'transientSubscriptionPackage' ) );
			
			self._dispatchClaim();
		} else if ( rules.token && rules.subscription && _.isNumber( rules.subscriptionPackage ) ) {
			// if unit has token and subscription, and package is a number (because 0 is allowed), do the transfer
			// subscription routine.
			
			self._dispatchTransfer();
		} else if ( 'HOME' === self.family && self.user.hasRoles( [ 'INSTALLER_HOME', ] ) && !self.installer ) {
			// home check for privileges
			
			// check for installer!
			self._showSelectInstaller( {
				context: 'home',
			} );
		} else {
			self._dispatchSubscription();
		}
	},
	
	/**
	 * Dispatch protocol for unit transfers.
	 * @void
	 */
	_dispatchTransfer: function () {
		let self = this;
		let fn   = false;
		
		// show the subscription details to the user.
		if ( !self.acceptTransfer ) {
			fn = self._showClaimTransfer;
		}
		
		// the user must be logged in
		if ( self.acceptTransfer && !self.loggedIn ) {
			fn = self._showUserLogin;
		}
		
		// make the user select a customer
		if ( self.acceptTransfer && self.loggedIn && !self.owner ) {
			fn = self._showSelectOwner;
		}
		
		// make the user accept the terms and conditions
		if ( self.acceptTransfer && self.loggedIn && self.owner && !self.acceptTerms ) {
			fn = self._showClaimTerms;
		}
		
		// transfer the subscription.
		if ( self.acceptTransfer && self.loggedIn && self.owner && self.acceptTerms ) {
			fn = self._transferSubscription;
		}
		
		
		// call the set function
		if ( _.isFunction( fn ) ) {
			fn.call( this );
		}
	},
	
	/**
	 * The dispatch protocol for creating a new subscription.
	 * @void
	 */
	_dispatchSubscription: function () {
		let self = this;
		let fn   = false;
		
		// if a home unit is set, just set name to alias and some of the automotive-only vars to true
		if ( 'HOME' === self.family ) {
			self.name            = self.name || self.model.get( 'alias' );
			self.alarmSelected   = true;
			self.prepaidSelected = true;
		}
		
		// if an automotive unit is set, set name by filling out vehicledata
		if ( !self.name && 'AUTOMOTIVE' === self.family ) {
			if ( !self.vehicleTypes ) {
				fn = self._getVehicleTypes;
			} else {
				fn = self._showSettingsAutomotiveForm;
			}
			
		}
		
		// if no alarm is selected, do the alarm group flow
		if ( self.name && !self.alarmSelected ) {
			fn = self._showAlarmGroupFlow;
		}
		
		// if no prepaid is selected do the prepaid subscription package flow.
		if ( self.name && self.alarmSelected && !self.prepaidSelected ) {
			fn = self._showPrepaidFlow;
		}
		
		// show the actual claim view
		if ( self.name && self.alarmSelected && self.prepaidSelected && !self.forward ) {
			
			// set the claim option for some cases.
			let customers  = self.user.getCustomers();
			let installers = [];
			
			if ( 'AUTOMOTIVE' === self.family ) {
				installers = customers.filterByRoles( [ 'INSTALLER_AUTOMOTIVE', ] );
			}
			
			if ( 'HOME' === self.family ) {
				installers = customers.filterByRoles( [ 'INSTALLER_HOME', ] );
			}
			
			// does the current user have one or more customers, and ONLY installer customers?
			if ( customers.length >= 1 && customers.length === installers.length ) {
				self.forward = 'forward';
				
				// does the current user have NO installers.
			} else if ( 0 === installers.length ) {
				self.forward = 'claim';
				
				// make the customer choose option.
			} else {
				fn = self._showClaimForwardOption;
			}
		}
		
		// at this point the forward must be set, so we don't check for name, alarm or prepaid anymore
		if ( 'claim' === self.forward ) {
			fn = self._dispatchClaim;
		}
		
		if ( 'forward' === self.forward ) {
			fn = self._dispatchForward;
		}
		
		// call the set function
		if ( _.isFunction( fn ) ) {
			fn.call( this );
		}
	},
	
	/**
	 * The dispatch for forwarding a unit to a customer.
	 * @void
	 */
	_dispatchForward: function () {
		let self    = this,
			fn      = false,
			context = 'forward';
		
		// show the installer his totals
		if ( !self.completed && !self.acceptTotals ) {
			
			if ( self.owner || self.prepaid || self.subscriptionPackage ) {
				fn = self._showClaimOverview;
			} else {
				self.acceptTotals = true;
			}
		}
		
		// the mail hasn't been sendt yet.
		if ( !self.completed && self.acceptTotals ) {
			fn = self._showForwardEmail;
		}
		
		// everything is done, show close-view.
		if ( self.completed ) {
			fn = self._showClaimInstallSuccess;
		}
		
		if ( _.isFunction( fn ) ) {
			fn.call( self, context );
		}
		
	},
	
	/**
	 * The dispatch for finalizing and claiming a subscription.
	 * @void
	 */
	_dispatchClaim: function () {
		let self = this;
		let fn   = false;
		
		// user must be logged in
		if ( !self.loggedIn ) {
			fn = self._showUserLogin;
		}
		
		// user must select a customer
		if ( self.loggedIn && !self.owner ) {
			fn = self._showSelectOwner;
		}
		
		// if there is an alarm central, make sure the owner at least got one phone number.
		if ( self.loggedIn && self.owner && !self.phone ) {
			fn = self._showAddPhoneNumber;
		}
		
		// user must select a subscription package.
		if ( self.loggedIn && self.owner && self.phone && !self.subscriptionSelection ) {
			fn = self._showSubscriptionPackageSelection;
		}
		
		// user must accept the totals
		if ( self.loggedIn && self.owner && self.phone && self.subscriptionSelection && !self.acceptTotals ) {
			fn = self._showClaimOverview;
		}
		
		// user must accept the terms and conditions
		if ( self.loggedIn && self.owner && self.phone && self.subscriptionSelection && self.acceptTotals && !self.acceptTerms ) {
			fn = self._showClaimTerms;
		}
		
		// create the subscription for the user
		if ( self.loggedIn && self.owner && self.phone && self.subscriptionSelection && self.acceptTotals && self.acceptTerms ) {
			fn = self._createSubscription;
		}
		
		if ( _.isFunction( fn ) ) {
			fn.call( self );
		}
	},
	
	// - vehicle
	
	_getVehicleTypes: function () {
		
		this.options.parent.busy();
		
		this.listenToOnce( this.model, 'done', function ( response ) {
			this.stopListening( this.model, 'error' );
			this.options.parent.idle();
			this.vehicleTypes = response;
			this.dispatch();
		} );
		this.listenToOnce( this.model, 'error', function ( response ) {
			channelLayout.trigger( 'error', response.responseJSON );
		} );
		
		this.model.fetchVehicleTypes();
		
	},
	
	
	/**
	 * Displays the vehicle details.
	 * @void
	 */
	_showSettingsAutomotiveForm: function () {
		
		let self = this;
		
		let settingsAutomotiveForm = new SettingsAutomotiveForm( _.extend( this.options, {
			context       : 'install',
			initValidation: true,
			vehicleTypes  : self.vehicleTypes,
			model         : self.settingsAutomotive,
		} ) );
		
		// when settings automotive syncs
		self.listenToOnce( self.settingsAutomotive, 'sync', function ( model ) {
			
			// keep busy
			settingsAutomotiveForm.busy();
			
			self.model.set( 'transientSettingsAutomotive', model.get( 'id' ) );
			
			// listening for the Unit sync
			self.listenToOnce( self.model, 'sync', function ( unit ) {
				// set the name and trigger dispatch
				self.name = unit.get( 'alias' );
				self.dispatch();
			} );
			
			// catch any Unit error and send it to the view.
			self.listenToOnce( self.model, 'error', function ( model, response ) {
				settingsAutomotiveForm.outputFeedback( {
					status  : 'error',
					response: response,
				} );
			} );
			
			// save the unit alias from the model
			let options = {
				alias: model.name()
			};
			
			if ( self.options.token ) {
				_.extend( options, {
					token: self.options.token
				} );
			}
			
			self.model.saveAlias( options );
			
		} );
		
		// attach to DOM
		self.showChildView( 'container', settingsAutomotiveForm );
	},
	
	// - user and customer
	
	/**
	 * Shows the login view.
	 * @void
	 */
	_showUserLogin: function () {
		let self = this;
		
		self.currentUser = new User();
		
		let userContainer = new UserContainer( {
			user       : self.user,
			currentUser: self.currentUser,
			email      : this.options.email,
			view       : this.options.context,
			token      : this.options.token || '',
			navHandler : 'internal',
		} );
		
		// if end user creates a new customer, show the happy ending.
		self.listenToOnce( self.currentUser, 'sync', function ( model ) {
			userContainer.settings( {
				view: 'user:thank-you',
			} );
		} );
		
		self.listenToOnce( self.user, 'sync', function ( model ) {
			
			// installers get rollback to earlier alternatives
			if ( model.hasRoles( [ 'INSTALLER_AUTOMOTIVE', 'INSTALLER_HOME', ] ) ) {
				self.forward         = false;
				self.alarmSelected   = false;
				self.prepaidSelected = false;
			}
			
			self.dispatch();
			
		} );
		self.showChildView( 'container', userContainer );
	},
	
	/**
	 * Shows selection of installer-customer.
	 * @void
	 */
	_showSelectInstaller: function ( options ) {
		
		let self = this,
			role = '';
		
		switch ( options.context ) {
			
			case 'automotive' :
				role = 'INSTALLER_AUTOMOTIVE';
				break;
			
			case 'home' :
				role = 'INSTALLER_HOME';
				break;
			
			case 'security' :
				role = 'INSTALLER_AUTOMOTIVE_DENMARK_SECURITY';
				break;
		}
		
		let filteredInstallers = self.installers.filterByRoles( [ role, ] );
		
		// this should never trigger!
		if ( 0 === filteredInstallers.length ) {
			self.alarmSelected = true;
			self.dispatch();
		}
		
		// has one customer, select that customer.
		if ( 1 === filteredInstallers.length ) {
			self.installer = filteredInstallers.first().get( 'id' );
			self.dispatch();
		}
		
		// has more customers, show customer selection.
		if ( filteredInstallers.length >= 2 ) {
			let customerContainer = new CustomerContainer( {
				view      : 'customer:list',
				user      : self.user,
				context   : 'installer',
				create    : false,
				customers : filteredInstallers,
				navHandler: 'internal',
			} );
			
			self.showChildView( 'container', customerContainer );
		}
	},
	
	/**
	 * Shows selection of owner-customer.
	 * @void
	 */
	_showSelectOwner: function () {
		
		let customer  = !1, // will be set based on number of existing customers.
			customers = this.user.getCustomers();
		
		// if there is only one customer, preselect it and return to dispatch.
		if ( 1 === customers.length ) {
			this.owner = customers.first().get( 'id' );
			this.dispatch();
			
			// if there is 0 or 2+ customers, we need to use the layout.
		} else {
			
			// select likely preferred view
			let view = ( 0 === customers.length )
					   ? 'customer:create'
					   : 'customer:list';
			
			// create and select a customer flow
			
			customer = new Customer();
			
			this.listenToOnce( customer, 'sync', function () {
				
				this.owner = customer.get( 'id' );
				channel.request( 'customers' ).add( customer, { merge: true } );
				
				if ( _.where( defa.vars.mapUsersCustomers, { user: this.user.get( 'id' ), customer: this.owner, } ).length !== 1 ) {
					defa.vars.mapUsersCustomers.push( {
						user    : this.user.get( 'id' ),
						customer: this.owner,
						roles   : [ 'CUSTOMER', ],
					} );
				}
				
				this.dispatch();
			} );
			
			// add the customer layout.
			let customerContainer = new CustomerContainer( {
				view      : view,
				customer  : customer,
				userId    : this.user.get( 'id' ),
				user      : this.user,
				context   : 'customer',
				create    : true,
				customers : customers,
				navHandler: 'internal',
			} );
			
			// add to viewport
			this.showChildView( 'container', customerContainer );
		}
	},
	
	/**
	 * Displays a request to add a phone number if needed.
	 * @void
	 */
	_showAddPhoneNumber: function () {
		
		let self         = this,
			phoneNumbers = channel.request( 'phoneNumbers' ).where( { user: self.user.get( 'id' ) } );
		
		if ( self.model.get( 'transientAlarmCentral' ) && 0 === phoneNumbers.length ) {
			
			let phoneNumber = new PhoneNumber();
			
			this.listenToOnce( phoneNumber, 'sync', function ( model ) {
				self.phone = true;
				self.dispatch();
			} );
			
			let phoneNumberCreateForm = new PhoneNumberCreateForm( {
				model      : phoneNumber,
				currentUser: self.user,
			} );
			
			self.showChildView( 'container', phoneNumberCreateForm );
			
		} else {
			
			self.phone = true;
			self.dispatch();
		}
	},
	
	// - alarm group (insurance companies and alarm centrals)
	
	/**
	 * Shows alarm group claim flow.
	 * @void
	 */
	_showAlarmGroupFlow: function () {
		
		let self = this;
		
		// skip along if user doesn't have permissions to set alarm groups
		if ( !self.user.hasRoles( [ 'INSTALLER_AUTOMOTIVE_DENMARK_SECURITY' ] ) ) {
			self.alarmSelected = true;
			self.dispatch();
			
		} else {
			
			// select installer
			if ( !self.installer ) {
				self._showSelectInstaller( {
					context: 'security',
				} );
				
				// do the alarm group flow
			} else {
				let alarmGroupFlow = new defa.views.AlarmGroupFlow( {
					unit              : self.model,
					customer          : channel.request( 'customers' ).get( self.installer ),
					settingsAutomotive: self.settingsAutomotive,
				} );
				
				this.showChildView( 'container', alarmGroupFlow );
			}
		}
	},
	
	/**
	 * Shows selection of insurance companies
	 * @void
	 */
	_showInsuranceCompanySelection: function () {
		
		let insuranceCompanies = channel.request( 'insuranceCompanies' ).filterByIds( this.alarmGroups.pluck( 'insuranceCompany' ) );
		
		let insuranceCompanyContainer = new defa.views.InsuranceCompanyContainer( {
			collection: insuranceCompanies,
		} );
		
		this.showChildView( 'container', insuranceCompanyContainer );
	},
	
	/**
	 *
	 * @void
	 */
	_showAlarmCentralSelection: function () {
		
		let self               = this,
			filteredAlarmGroup = new defa.collections.AlarmGroups( self.alarmGroups.where( { insuranceCompany: self.insuranceCompany } ) ),
			alarmCentrals      = channel.request( 'alarmCentrals' ).filterByIds( filteredAlarmGroup.pluck( 'alarmCentral' ) );
		
		let alarmCentralContainer = new defa.views.AlarmCentralContainer( {
			collection: alarmCentrals,
		} );
		
		self.showChildView( 'container', alarmCentralContainer );
	},
	
	/**
	 * Saves alarmGroup, installer and service to settingsAutomotive, and updates the unit model.
	 * @void
	 */
	_saveAlarmGroup: function () {
		
		let self = this;
		
		// set current view in busy-state.
		self.getChildView( 'container' ).busy();
		
		// get the correct alarmGroup.
		let alarmGroup = self.alarmGroups.where( {
			insuranceCompany: self.insuranceCompany,
			alarmCentral    : self.alarmCentral,
		} )[ 0 ];
		
		// when settingsAutomotive is saved, then save the model.
		self.listenTo( self.settingsAutomotive, 'sync', function () {
			
			self.options.token = self.settingsAutomotive.get( 'token' );
			
			self.model.set( {
				transientInsuranceCompany   : self.insuranceCompany,
				transientAlarmCentral       : self.alarmCentral,
				transientSubscriptionPackage: false,
			} );
			
			self.alarmSelected = true;
			self.dispatch();
			
		} );
		
		// save the alarmGroup.
		self.settingsAutomotive.saveAlarmGroup( {
			alarmGroup        : alarmGroup.get( 'id' ),
			transientInstaller: self.installer,
			transientService  : self.service || false,
		} );
		
	},
	
	// - subscription package handling
	
	/**
	 * Shows the prepaid subscription package flow.
	 * @void
	 */
	_showPrepaidFlow: function () {
		
		let self = this;
		
		// skip along if user doesn't have permissions to set prepaid subscription packages
		if ( !self.user.hasRoles( [ 'INSTALLER_AUTOMOTIVE', ] ) ) {
			self.prepaidSelected = true;
			self.dispatch();
			
		} else {
			
			// select installer
			if ( !self.installer ) {
				self._showSelectInstaller( {
					context: 'automotive',
				} );
				
				// do the prepaid subscription package flow
			} else {
				
				// don't start the flow, unless we've got packages to select from
				self.subscriptionPackages = new defa.collections.SubscriptionPackages();
				
				self.listenToOnce( self.subscriptionPackages, 'sync', function () {
					
					// if no packages exist, skip the subscription package flow
					if ( 0 === self.subscriptionPackages.length ) {
						self.prepaidSelected = true;
						self.dispatch();
						
						// if packages exist, show the options
					} else {
						// everything is fine, create and attach the subscription package flow
						let subscriptionPackagePrepaidFlow = new defa.views.SubscriptionPackagePrepaidFlow( {
							unit                : self.model,
							installer           : channel.request( 'customers' ).get( self.installer ),
							subscriptionPackages: self.subscriptionPackages,
						} );
						
						this.showChildView( 'container', subscriptionPackagePrepaidFlow );
					}
				} );
				
				// fetch prepaid packages
				self.subscriptionPackages.fetch( {
					data       : {
						prepaid   : true,
						unitId    : self.model.get( 'id' ),
						customerId: self.installer,
					},
					processData: true,
				} );
			}
		}
	},
	
	/**
	 * Shows a view of several subscription packages.
	 * @void
	 */
	_showSubscriptionPackageSelection: function () {
		
		this.subscriptionPackages = new defa.collections.SubscriptionPackages();
		
		this.listenToOnce( this.subscriptionPackages, 'sync', function () {
			
			this.stopListening( this.subscriptionPackages, 'error' );
			
			let subscriptionPackageContainer = new defa.views.SubscriptionPackageContainer( {
				owner     : this.owner,
				prepaid   : false,
				unit      : this.model,
				collection: this.subscriptionPackages,
			} );
			
			this.showChildView( 'container', subscriptionPackageContainer );
		} );
		
		this.listenToOnce( this.subscriptionPackages, 'error', function ( collection, response ) {
			channelLayout.trigger( 'error', response.responseJSON );
		} );
		
		this.subscriptionPackages.fetch( {
			data       : {
				prepaid   : false,
				unitId    : this.model.get( 'id' ),
				customerId: this.owner,
			},
			processData: true,
		} );
		
	},
	
	// - claim methods
	
	/**
	 * Shows a view allowing the user to either forward or claim the unit.
	 * @void
	 */
	_showClaimForwardOption: function () {
		let claimForwardOptionForm = new ClaimForwardOptionForm( {
			model: this.model,
		} );
		this.showChildView( 'container', claimForwardOptionForm );
	},
	
	/**
	 * Shows the overview of selected options ( customer, subscriptionPackage, unit ).
	 * @void
	 */
	_showClaimOverview: function ( context ) {
		
		let claimOverviewForm = new ClaimOverviewForm( {
			model              : this.model,
			customer           : channel.request( 'customers' ).get( this.owner ),
			context            : context || false,
			prepaid            : this.prepaidSelected,
			subscriptionPackage: channel.request( 'subscriptionPackages' ).get( this.subscriptionPackage ),
		} );
		
		this.showChildView( 'container', claimOverviewForm );
	},
	
	/**
	 * Shows the transfer unit view.
	 * @void
	 */
	_showClaimTransfer: function () {
		let self = this;
		
		// no token, no transfer:
		if ( !self.options.token ) {
			self.trigger( 'nav', 'unit:update', [ self.model.get( 'id' ), ] );
			
			// yes token, yes transfer-view
		} else {
			self.subscription        = channel.request( 'subscriptions' ).get( self.model.get( 'transientSubscription' ) );
			self.prevOwner           = channel.request( 'customers' ).get( self.subscription.get( 'owner' ) );
			self.subscriptionPackage = channel.request( 'subscriptionPackages' ).get( self.model.get( 'transientSubscriptionPackage' ) );
			
			let claimTransferForm = new ClaimTransferForm( {
				model              : self.model,
				owner              : self.prevOwner,
				subscription       : self.subscription,
				subscriptionPackage: self.subscriptionPackage,
			} );
			
			self.showChildView( 'container', claimTransferForm );
		}
	},
	
	/**
	 * Shows the view for forwarding an installation by email.
	 * @void
	 */
	_showForwardEmail: function () {
		
		let claimForwardEmailForm = new ClaimForwardEmailForm( {
			model: this.model,
		} );
		
		this.showChildView( 'container', claimForwardEmailForm );
	},
	
	/**
	 * Shows the terms and conditions for a defa unit subscription.
	 * @void
	 */
	_showClaimTerms: function () {
		let claimTermsForm = new ClaimTermsForm( {
			customer: channel.request( 'customers' ).get( this.owner ),
		} );
		this.showChildView( 'container', claimTermsForm );
		
	},
	
	/**
	 * Shows ending view, letting the installer know he is done.
	 * @void
	 */
	_showClaimInstallSuccess: function () {
		
		
		let successView = new SuccessView( {
			context  : 'unit:installed',
			model    : channel.request( 'customers' ).get( this.installer ),
			closeLink: {
				event : 'unit:installed:selected',
				params: [ this.installer, 1, ],
			},
		} );
		
		this.listenTo( successView, 'success:close', function () {
			successView.animateOut();
		} );
		
		this.showChildView( 'container', successView );
	},
	
	// - done methods
	
	/**
	 * Transfers the subscription to a new user.
	 * @void
	 */
	_transferSubscription: function () {
		
		let subscription = channel.request( 'subscriptions' ).get( this.model.get( 'transientSubscription' ) );
		
		// save the options - this triggers onSubscriptionRequest and onSubscriptionSync in the unit controller.
		subscription.saveOwner( {
			owner: this.owner,
			token: this.options.token,
		} );
	},
	
	/**
	 * Does the actual claim, connecting a unit and a customer with a subscription.
	 * @void
	 */
	_createSubscription: function () {
		
		// setup the options
		let options = {
			owner              : this.owner,
			unit               : this.model.get( 'id' ),
			promotionCode      : this.subscriptionPackage.get( 'promotionCode' ),
			subscriptionPackage: this.subscriptionPackage.get( 'id' ),
		};
		
		// add token if it exists
		if ( this.options.token ) {
			_.extend( options, { token: this.options.token } );
		}
		
		// save the options - this triggers onSubscriptionRequest and onSubscriptionSync in the unit controller.
		this.options.subscription.save( options );
	},
	
} );

export default ClaimFlow;